import { Component, computed, inject, OnInit, signal } from '@angular/core';

import { AddCardComponent } from '@webapp/payments/ui/components/add-card/add-card.component';
import { ActivatedRoute, Router } from '@angular/router';
import { QuoterService } from '@webapp/quoter/services/quoter.service';
import { firstValueFrom } from 'rxjs';
import { PaymentStoreService } from '../../../store/payment-store.service';
import { IPayment, PATMENT_TYPE_ENUM } from '@webapp/quoter/interfaces/payment.interface';
import { IQuoterPolicyEmition } from '@webapp/quoter/interfaces/quoter.interface';
import { PoliciesService } from '@webapp/policies/services/policies.service';
import { BackButtonComponent, ButtonComponent, LoadingOverlayComponent, SkeletonComponent } from '@webapp/ui';
import { IPaymentMethod } from '@webapp/payments/domain/payments';
import { PaymentMethodItemComponent } from "@webapp/payments/ui/components/payment-method-item/payment-method-item.component";
import { FlowResumeStepComponent } from '@webapp/policies/flows/issue-policy-flow';
import { RiskRegistry } from '@webapp/risk-core';
import { WEB_RISK_SLOTS } from '@webapp/risks/web';
import { DisclaimerComponent } from '@webapp/core/ui/components/disclaimer/disclaimer.component';
import { IClause, IEmbeddedQr } from '@webapp/qrs/interfaces/qrs.interface';
@Component({
  selector: 'app-payment-link-page',
  imports: [
    AddCardComponent,
    FlowResumeStepComponent,
    LoadingOverlayComponent,
    PaymentMethodItemComponent,
    BackButtonComponent,
    ButtonComponent,
    SkeletonComponent,
    DisclaimerComponent
  ],
  templateUrl: './payment-link-page.component.html',
  styleUrl: './payment-link-page.component.scss',
})
export class PaymentLinkPageComponent implements OnInit {
  activatedRoute = inject(ActivatedRoute);
  quoterService = inject(QuoterService);
  policiesService = inject(PoliciesService);
  paymentStoreService = inject(PaymentStoreService);
  router = inject(Router);
  private readonly riskRegistry = inject(RiskRegistry);
  paymentState = this.paymentStoreService.state.asReadonly();

  protected quoteUser = computed(() => this.paymentState().quote?.user ?? null);
  protected quoteCoverage = computed(() => this.paymentState().quote?.coverage ?? null);
  protected quoteCurrency = computed(() => this.paymentState().quote?.currency ?? 'ARS');
  protected quoteRiskType = computed(() => {
    const quote = this.paymentState().quote;
    const tag = quote?.risk ?? quote?.insurable?.type;
    return tag ? { tag } : null;
  });
  protected quoteInsurable = computed(() => {
    const quote = this.paymentState().quote;
    const insurable = quote?.insurable ?? null;
    if (!insurable) return null;
    return { ...insurable, currency: quote.currency };
  });

  // ── QR mandatory clauses (transient — read from the embedded qr object) ──

  private readonly qrClauses = computed<IClause[]>(() => {
    const insurable = this.paymentState().quote?.insurable as { qr?: IEmbeddedQr } | undefined;
    return insurable?.qr?.clauses ?? [];
  });

  readonly detailData = computed<unknown>(() => {
    const insurable = this.quoteInsurable();
    return insurable ? { ...insurable, qrClauses: this.qrClauses() } : null;
  });

  private token = '';
  loadingQuote = signal(true);
  loadingEmit = signal(false);
  showPaymentSection = signal(false);
  showAddCard = signal(false);
  showPaymentButton = signal(false);
  paymentMethods = signal<IPaymentMethod[]>([
    {
      id: 'mercado_pago_cash',
      name: 'Dinero en cuenta',
      type: 'money',
      lastFour: '',
      description: 'Dinero en cuenta - MercadoPago',
      isSelected: false,
      isEnabled: true
    },
    {
      id: 'credit_card',
      name: 'Tarjeta de crédito o débito',
      type: 'credit_card',
      lastFour: '',
      description: 'Tarjeta de crédito o débito - MercadoPago',
      isSelected: false,
      isEnabled: true
    }
  ]);

  /**
   * Non-null when the risk contributes an `insurable-detail` slot. The resume
   * step resolves and mounts the slot itself from the risk tag; this value only
   * tells it that the risk has a detail to show.
   */
  readonly detailComponentType = computed<string | null>(() => {
    const tag = this.quoteRiskType()?.tag;
    if (!tag) return null;
    return this.riskRegistry.slot('web', tag, WEB_RISK_SLOTS.INSURABLE_DETAIL) ? WEB_RISK_SLOTS.INSURABLE_DETAIL : null;
  });

  ngOnInit(): void {
    this.token = this.activatedRoute.snapshot.params['id'];
    this.getQuote();
  }

  

  private async getQuote() {
    this.loadingQuote.set(true);
    try {
      const quote = await firstValueFrom(
        this.quoterService.getPaymentLinkQuote(this.token)
      );

      this.paymentStoreService.set('quote', quote);
    } catch (error) {
      //this.router.navigateByUrl('error');
      throw new Error(`Error al obtener la cotización: ${error}`);
    } finally {
      this.loadingQuote.set(false);
    }
  }

  continue() {
    this.showPaymentSection.set(true);
  }

  backToResume() {
    this.showPaymentSection.set(false);
    this.showAddCard.set(false);
    this.showPaymentButton.set(false);
    this.paymentMethods.set(this.paymentMethods().map(pm => ({ ...pm, isSelected: false })));
  }

  goBack() {
    this.showAddCard.set(false);
    this.paymentMethods.set(this.paymentMethods().map(pm => ({
      ...pm,
      isSelected: false
    })));

  }

  onPaymentMethodSelected(payment: IPayment) {
    this.paymentMethods.set(this.paymentMethods().map(pm => ({
      ...pm,
      isSelected: pm.id === payment.id
    })));

    if (payment.type === 'credit_card') {
      this.showPaymentButton.set(false);
      this.showAddCard.set(true);
    } else {
      this.showPaymentButton.set(true);
    }
  }


  async emit(payment: IPayment) {
    await this.doEmit({ ...payment, type: 'card' });
  }

  async emitCash() {
    await this.doEmit({ type: 'account_money' });
  }

  private async doEmit(payment: IPayment) {
    this.loadingEmit.set(true);
    try {
      const { id, tokenlink, insurable } = this.paymentState().quote;
      const payload: IQuoterPolicyEmition = {
        coverageId: insurable.id,
        tokenLink: tokenlink,
        payment,
      };
      const response = await firstValueFrom(
        this.policiesService.emitPolicy(payload, id)
      );
      localStorage.setItem('activar_policy', JSON.stringify(response));
      if (payment.type === PATMENT_TYPE_ENUM.ACCOUNT_MONEY) {
        const initPoint = response.initPoint;
        if (initPoint) {
          window.location.href = response.initPoint ?? ''
        } else {
          console.error('Error al generar el link de pago');
        }

      } else {
        this.router.navigateByUrl('pago-exitoso');
      }

    } catch (error) {
      console.error('Error al emitir la póliza:', error);
    } finally {
      this.loadingEmit.set(false);
    }
  }
}
